import { City } from '@/core/collections/contracts-api/cities/city.interface';
import { Customer } from '@/core/collections/contracts-api/customers/customer.interface';
import store from '@/core/store';
import i18n from '@/i18n';
import { checkAddon, Team } from '@nxtpeople/nxt-vue-library';
import { computed, inject, ref } from 'vue';

export interface FieldKey {
   key: string;
   translation: string;
   display_key?: string;
   max_length?: number;
   required?: boolean;

   optionsUrl?: string;
   options?: Array<any>;
   customDisplayTemplate?: (option: any) => string;
   dropdownParams?: Record<string, any>;
}

export function useContractTemplateKeys() {
   const { locale } = useI18n();
   const customer = inject('customer', ref<Customer>());
   const company = computed(() => customer.value?.company);
   const { t } = useI18n();

   const COMPANY_KEYS = ['invoice_group_id', 'cost_center_id', 'department_id', 'work_station_id', 'fulltime_week_id', 'shift_id'];
   const CO_FIELD_KEYS = [
      'worker_statute_id',
      'worker_type_id',
      'shift_id',
      'invoice_group_id',
      'cost_center_id',
      'job_function_id',
      'recruitment_type_id',
   ];
   const CONTRACT_COLOR_OPTIONS = [
      '#E2E0EF',
      '#CFDDEA',
      '#E1DDCE',
      '#FFD233',
      '#C1E6CD',
      '#98D5F4',
      '#879FDB',
      '#BCAAD9',
      '#FC9BEC',
      '#EDB0B0',
      '#F0A259',
   ];
   const COLOR = computed<Array<FieldKey>>(() => [
      {
         key: 'color',
         translation: i18n.global.t('generic.color'),
         options: CONTRACT_COLOR_OPTIONS,
      },
   ]);
   const WHO_WHAT_WHERE_FIELD_KEYS = computed<Array<FieldKey>>(() => [
      {
         key: 'team_id',
         translation: i18n.global.t('generic.team'),
         customDisplayTemplate: (team: Team) => (team.reference ? `${team.name} (Ref: ${team.reference})` : `${team.name}`),
      },
      {
         key: 'invoice_group_id',
         translation: i18n.global.t('companies.invoice-groups.invoice-group'),
         display_key: 'name',
         options: company.value?.invoice_groups || [],
      },
      {
         key: 'cost_center_id',
         translation: i18n.global.t('companies.cost-centers.cost-center'),
         display_key: 'name',
         options: company.value?.cost_centers || [],
      },
      {
         key: 'department_id',
         translation: i18n.global.t('companies.departments.department'),
         display_key: 'name',
         options: company.value?.departments || [],
      },
      {
         key: 'worker_statute_id',
         translation: i18n.global.t('contracts.statute'),
         display_key: `name.${i18n.global.locale.value}`,
      },
      {
         key: 'worker_type_id',
         translation: i18n.global.t('contracts.worker-type'),
         display_key: `name.${i18n.global.locale.value}`,
      },
   ]);
   const WHEN_FIELD_KEYS = computed<Array<FieldKey>>(() => [
      {
         key: 'fulltime_week_id',
         translation: i18n.global.t('companies.fulltimes.fulltime'),
         display_key: 'created_at',
         options: company.value?.fulltimes || [],
         dropdownParams: {
            groupDisplayKey: 'fulltime.name',
            groupUniqueKey: 'fulltime.id',
            grouped: true,
         },
         customDisplayTemplate: (fulltimeWeek: any) => {
            if (fulltimeWeek.fulltime) {
               return `${t('general.weeks')} ${fulltimeWeek.week_number} (${fulltimeWeek.fulltime.name} )`;
            }
            return fulltimeWeek.name;
         },
      },
      { key: 'shift_id', translation: i18n.global.t('companies.shifts.shift'), display_key: 'name', options: company.value?.shifts || [] },
      {
         key: 'employment_type',
         translation: i18n.global.t('contracts.employment-type'),
         required: true,
         options: [
            { value: '2-1-2', label: '2-1-2' },
            { value: 'day', label: i18n.global.t('contracts.day-contract') },
            { value: 'week', label: i18n.global.t('contracts.week-contract') },
         ]
            .filter((option) => (customer.value?.are_day_contracts_allowed ? true : option.value !== 'day'))
            .filter((option) => (option.value === '2-1-2' ? checkAddon('2-1-2-employment', store.state.userInfo?.userInfo?.addons) : true)),
      },
   ]);

   const ADDRESS_FIELD_KEYS = computed<Array<FieldKey>>(() => [
      { key: 'employment_street', translation: i18n.global.t('generic.street') },
      { key: 'employment_house_number', translation: i18n.global.t('generic.house-number') },
      { key: 'employment_box', translation: i18n.global.t('generic.box') },
      {
         key: 'employment_city_id',
         translation: i18n.global.t('generic.city'),
         display_key: `name.${i18n.global.locale.value}`,
         customDisplayTemplate: (city: City) => {
            const cityName = (city.name as any)?.[locale.value] ?? city.name;
            const countryName = (city.country?.name as any)?.[locale.value] ?? city.country?.name;
            return city.country ? `${cityName} - ${city.postal_code} (${countryName})` : `${cityName} - ${city.postal_code}`;
         },
         optionsUrl: 'city/searchCities',
      },
   ]);
   const FUNCTION_DATA_FIELD_KEYS = computed<Array<FieldKey>>(() => [
      {
         key: 'job_function_id',
         translation: i18n.global.t('contracts.job-function'),
         display_key: `preferred_label`,
         optionsUrl: 'jobFunction/searchJobFunctions',
      },
      {
         key: 'employment_motive_id',
         translation: i18n.global.t('contracts.worker-motive'),
         display_key: `name.${i18n.global.locale.value}`,
         options: customer.value?.allowed_employment_motives || [],
      },
      { key: 'replacement_motive_id', translation: i18n.global.t('contracts.reason'), display_key: `description` },
      { key: 'salary', translation: i18n.global.t('contracts.salary') },
      {
         key: 'salary_type_id',
         translation: i18n.global.t('contracts.salary-type'),
         display_key: `name.${i18n.global.locale.value}`,
         options: store.state.salaryType?.salaryTypes || [],
      },
      {
         key: 'salary_include_id',
         translation: i18n.global.t('generic.inclusive'),
         display_key: `name.${i18n.global.locale.value}`,
         options: store.state.salaryInclude?.salaryIncludes || [],
      },
   ]);
   const COMMENT_FIELD_KEYS = computed<Array<FieldKey>>(() => [{ key: 'comment', translation: i18n.global.t('generic.comment') }]);

   const PERFORMANCE_DATA_FIELD_KEYS = computed<Array<FieldKey>>(() => [
      {
         key: 'recruitment_type_id',
         translation: i18n.global.t('contracts.recruitment-type'),
         display_key: `name.${i18n.global.locale.value}`,
         options: store.state.recruitmentType?.recruitmentTypes || [],
      },
      {
         key: 'activity_risk_id',
         translation: i18n.global.t('contracts.activity-risk'),
         display_key: `name.${i18n.global.locale.value}`,
         options: store.state.activityRisk?.activityRisks || [],
      },
      { key: 'badge_number', translation: i18n.global.t('contracts.badge-number') },
      {
         key: 'shift_type_id',
         translation: i18n.global.t('contracts.shift-type'),
         display_key: `name.${i18n.global.locale.value}`,
         options: store.state.shiftType?.shiftTypes || [],
      },
   ]);
   const INVOICE_COMMENT_FIELD_KEYS = computed<Array<FieldKey>>(() => [
      { key: 'invoice_comment', translation: i18n.global.t('contracts.invoice-comment'), max_length: 25 },
   ]);
   const ALL_KEYS = computed(() => [
      ...COLOR.value,
      ...ADDRESS_FIELD_KEYS.value,
      ...COMMENT_FIELD_KEYS.value,
      ...FUNCTION_DATA_FIELD_KEYS.value,
      ...PERFORMANCE_DATA_FIELD_KEYS.value,
      ...WHEN_FIELD_KEYS.value,
      ...WHO_WHAT_WHERE_FIELD_KEYS.value,
      ...INVOICE_COMMENT_FIELD_KEYS.value,
   ]);
   return {
      COMPANY_KEYS,
      CO_FIELD_KEYS,
      CONTRACT_COLOR_OPTIONS,

      // reactive
      COLOR,
      ADDRESS_FIELD_KEYS,
      COMMENT_FIELD_KEYS,
      FUNCTION_DATA_FIELD_KEYS,
      PERFORMANCE_DATA_FIELD_KEYS,
      WHEN_FIELD_KEYS,
      WHO_WHAT_WHERE_FIELD_KEYS,
      INVOICE_COMMENT_FIELD_KEYS,
      ALL_KEYS,
   };
}
